// touchbase_frontend/src/features/scope_module/components/StatusBar.js
import React, { useEffect, useState } from 'react';
import { useScope } from '../../../contexts/DemoScopeContext';

const formatTime = (ms) => {
  const totalSeconds = Math.floor((ms || 0) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2,'0')}:${seconds.toString().padStart(2,'0')}`;
};

function StatusBar() {
  const { isTracking, elapsedTime, statusMessage, getHudMousePosition, isSyntheticPreview, isImagePairPreview, sessionDetails } = useScope();

  // Local-only mouse display; does NOT touch provider state (avoids global re-renders)
  const [mouseXY, setMouseXY] = useState({ x: '---', y: '---' });

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const pos = getHudMousePosition ? getHudMousePosition() : { x: null, y: null };
      const x = Number.isFinite(pos?.x) ? pos.x : '---';
      const y = Number.isFinite(pos?.y) ? pos.y : '---';
      setMouseXY((prev) => (prev.x === x && prev.y === y) ? prev : { x, y });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [getHudMousePosition]);

  // Synthetic preview mode: simplified status bar
  if (isSyntheticPreview) {
    return (
      <div className="status-bar status-bar-synthetic">
        <div className="status-left-metrics">
          <div className="status-indicator">
            <span>Mode:</span>
            <span className="status-synthetic-mode">Preview Only</span>
          </div>
          <div className="status-indicator">
            <span>X: {mouseXY.x}</span>
            <span>Y: {mouseXY.y}</span>
          </div>
        </div>
        <div className="status-right-message">
          <div className="status-message-item status-info">
            <span>
              {sessionDetails?.syntheticPairType === 'exclusion' ? 'Exclusion Pair' : 'Identification Pair'}
              {sessionDetails?.designatedConclusion && ` • ${sessionDetails.designatedConclusion}`}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Image pair preview mode: simplified status bar
  if (isImagePairPreview) {
    return (
      <div className="status-bar status-bar-image-pair">
        <div className="status-left-metrics">
          <div className="status-indicator">
            <span>Mode:</span>
            <span className="status-image-pair-mode">Preview Only</span>
          </div>
          <div className="status-indicator">
            <span>X: {mouseXY.x}</span>
            <span>Y: {mouseXY.y}</span>
          </div>
        </div>
        <div className="status-right-message">
          <div className="status-message-item status-info">
            <span>
              {sessionDetails?.setName || 'Image Pair'}
              {sessionDetails?.designatedConclusion && ` • ${sessionDetails.designatedConclusion}`}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="status-bar">
      <div className="status-left-metrics">
        <div className="status-indicator">
          <span>Tracking:</span>
          <div className={`status-dot ${isTracking ? 'active' : 'inactive'}`} />
          <span>{isTracking ? 'ON' : 'OFF'}</span>
        </div>
        <div className="status-indicator">
          <span>Time:</span>
          <span>{formatTime(elapsedTime)}</span>
        </div>
        <div className="status-indicator">
          <span>X: {mouseXY.x}</span>
          <span>Y: {mouseXY.y}</span>
        </div>
      </div>

      <div className="status-right-message">
        <div className={`status-message-item status-${statusMessage?.type || 'info'}`}>
          <span>{statusMessage?.text || ''}</span>
        </div>
      </div>
    </div>
  );
}

export default StatusBar;
